import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import Toast from '../components/Toast';
import { jsonGet, jsonPost, jsonPut } from '../api/jsonRequest';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { usePollQuery } from '../query/usePollQuery';

/** Public Botmark Inc. contact info (no auth) for estimates and marketing pages. */
export function useBotmarkCompanyInfo({ enabled = true } = {}) {
    return useQuery({
        queryKey: queryKeys.companies.botmarkInfo(),
        queryFn: async () => {
            const response = await axios.get('/api/Company/GetBotmarkCompanyInfo');
            return response.data;
        },
        enabled,
        staleTime: 5 * 60 * 1000,
    });
}

export function useCompaniesList({
    startDate,
    endDate,
    enabled = true,
    pauseRefetch = false,
} = {}) {
    const isFiltered = Boolean(startDate && endDate);
    const url = isFiltered
        ? buildPollUrl('/api/Company/GetAllCompaniesFiltered', startDate, endDate)
        : '/api/Company/GetAllCompanies';

    return usePollQuery({
        queryKey: isFiltered
            ? queryKeys.companies.filtered(startDate, endDate)
            : queryKeys.companies.list(),
        url,
        enabled,
        pauseRefetch,
    });
}

export function useCompany({
    companyId,
    enabled = true,
    pauseRefetch = false,
}) {
    const url = buildPollUrl('/api/Company/GetCompany', companyId);

    return usePollQuery({
        queryKey: queryKeys.companies.detail(companyId),
        url,
        enabled: Boolean(companyId) && enabled,
        pauseRefetch,
    });
}

export function useProcessorCompanies({ enabled = true, pauseRefetch = false } = {}) {
    return usePollQuery({
        queryKey: queryKeys.companies.processorList(),
        url: '/api/Company/GetProcessorCompany',
        enabled,
        pauseRefetch,
    });
}

async function refreshUserCompanies(userId) {
    if (!userId) {
        return null;
    }
    return jsonGet(`/api/Company/GetAllCompaniesForUser/${userId}`);
}

function syncUserCompanies(dispatch, companies) {
    if (companies) {
        dispatch({ type: 'USER_COMPANIES', payload: companies });
    }
}

export function useCreateCompany() {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ company, userId, roleId }) => {
            const res = await jsonPost(
                `/api/Company/CreateCompany/${userId}/${roleId}`,
                company,
            );
            if (res.status !== 200) {
                throw new Error('Creation failed');
            }
            return refreshUserCompanies(userId);
        },
        onSuccess: (companies) => {
            syncUserCompanies(dispatch, companies);
            Toast.fire({
                title: 'Company created',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Creation failed',
                icon: 'error',
            });
        },
    });
}

export function useUpdateCompany() {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ company, userId }) => {
            const res = await jsonPut('/api/Company/UpdateCompany', company);
            if (res.status !== 200) {
                throw new Error('Company Not Updated');
            }
            return refreshUserCompanies(userId);
        },
        onSuccess: (companies) => {
            syncUserCompanies(dispatch, companies);
            Toast.fire({
                title: 'Company Updated',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all() });
        },
        onError: () => {
            Toast.fire({
                title: 'Company Not Updated',
                icon: 'error',
            });
        },
    });
}

const COMPANY_PRIVILEGE_API_KEYS = {
    processExternal: 'ProcessExternal',
    creditEnabled: 'CreditEnabled',
};

/** Assign defined privilege fields; optional keyMap remaps client keys to API PascalCase. */
function assignDefinedCompanyPrivileges(target, { processExternal, creditEnabled }, keyMap = null) {
    if (processExternal !== undefined) {
        target[keyMap?.processExternal ?? 'processExternal'] = processExternal;
    }
    if (creditEnabled !== undefined) {
        target[keyMap?.creditEnabled ?? 'creditEnabled'] = creditEnabled;
    }
    return target;
}

function applyCompanyPrivilegePatch(company, { companyId, processExternal, creditEnabled }) {
    if (!company || company.id !== companyId) {
        return company;
    }

    return assignDefinedCompanyPrivileges({ ...company }, { processExternal, creditEnabled });
}

function patchCompaniesQueryData(old, variables) {
    if (old == null) {
        return old;
    }
    if (Array.isArray(old)) {
        return old.map((company) => applyCompanyPrivilegePatch(company, variables));
    }
    return applyCompanyPrivilegePatch(old, variables);
}

export function useUpdateCompanyPrivileges() {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ companyId, processExternal, creditEnabled, userId }) => {
            const payload = assignDefinedCompanyPrivileges(
                { Id: companyId },
                { processExternal, creditEnabled },
                COMPANY_PRIVILEGE_API_KEYS,
            );

            const res = await jsonPut('/api/Company/UpdateCompanyPrivileges', payload);
            if (res.status !== 200) {
                throw new Error('Company privilege not updated');
            }
            return refreshUserCompanies(userId);
        },
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.companies.all() });

            const previousCompaniesQueries = queryClient.getQueriesData({
                queryKey: queryKeys.companies.all(),
            });

            queryClient.setQueriesData(
                { queryKey: queryKeys.companies.all() },
                (old) => patchCompaniesQueryData(old, variables),
            );

            return { previousCompaniesQueries };
        },
        onSuccess: (companies) => {
            syncUserCompanies(dispatch, companies);
            Toast.fire({
                title: 'Company privilege updated',
                icon: 'success',
            });
            void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all() });
        },
        onError: (_error, _variables, context) => {
            context?.previousCompaniesQueries?.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
            Toast.fire({
                title: 'Company privilege not updated',
                icon: 'error',
            });
        },
    });
}

export function useDelistRelistCompany() {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    return useMutation({
        mutationFn: async ({ companyId, userId }) => {
            const res = await jsonPut(`/api/Company/DelistRelistCompany/${companyId}`, null);
            if (res.status !== 200) {
                throw new Error('Delist/relist failed');
            }
            const companies = await refreshUserCompanies(userId);
            return { data: res.data, companies };
        },
        onSuccess: ({ companies }) => {
            syncUserCompanies(dispatch, companies);
            void queryClient.invalidateQueries({ queryKey: queryKeys.companies.all() });
        },
    });
}
